import React, { useCallback, useState } from 'react';
import * as Styles from './ExternalDomain.styles';
import {
  Button, ChipCheckbox,
  IconButton,
  IconChevron,
  IconExternalLink,
} from 'gatorcomponents';
import PropTypes from 'prop-types';
import { optionsList, PROVIDERS } from './ExternalDomain.types';
import { copyTextToClipboard } from '@/utils/Application/copyTextToClipboard';
import { useDispatch } from 'react-redux';
import { commonActions } from '@/redux/modules';
import { useResize } from '@/hooks/useResize';

const ExternalDomain = ({
  externalDomainLocale,
  originalNameServers,
  hostNameServers,
  newDomain,
  returnBack,
  continueToSuccess,
  continueLoading,
}) => {
  const [selectedChip, setSelectedChip] = useState(null);

  const continueButtonDisabled = !selectedChip || continueLoading;

  const dispatch = useDispatch();
  const windowRect = useResize();

  const { width } = windowRect;
  const mobile = width <= 960;

  const selectedChipLabel = selectedChip ? externalDomainLocale.providers[selectedChip].label : '';

  const redirectToProvider = () => {
    window.open(externalDomainLocale.providers[selectedChip].link, '_blank');
  };

  const handleReturnBack = () => {
    returnBack && returnBack();
  };

  const handleContinue = () => {
    continueToSuccess && continueToSuccess();
  };

  const showCopyNotification = useCallback(() => {
    dispatch(commonActions.notifications.set({
      toast: {
        title: externalDomainLocale.copySuccess,
        info: '',
        showIcon: true,
        context: 'positive',
      },
    }));
  }, [dispatch, externalDomainLocale]);

  const handleCopy = (text) => {
    copyTextToClipboard(text);
    showCopyNotification();
  };

  const filteredOptionsList = newDomain && newDomain.value.includes('.br')
    ? optionsList
    : optionsList.filter(provider => provider !== 'registrobr');

  const providersChips = filteredOptionsList.map(providerKey => ({
    label: externalDomainLocale.providers[providerKey].label,
    checked: selectedChip === providerKey,
    id: providerKey,
    value: providerKey,
  }));

  const handleProviderChipClick = (e) => {
    setSelectedChip(e.value);
  };

  return (
    <Styles.ComponentWrapper data-testid="external-domain">
      <Styles.Title>
        {externalDomainLocale.titleLabel}
      </Styles.Title>
      <Styles.Description>
        {externalDomainLocale.descriptionLabel}
      </Styles.Description>

      <Styles.WizardContainer>
        <Styles.WhereFromLabel>
          {externalDomainLocale.whereFrom}
        </Styles.WhereFromLabel>

        <Styles.ChipsWrapper>
          {providersChips.map((chip, index) => (
            <ChipCheckbox
              index={index}
              key={chip.id}
              id={chip.id}
              label={chip.label}
              checked={chip.value === selectedChip}
              value={chip.value}
              onMark={handleProviderChipClick}
              variant="secondary"
            />
          ))}
        </Styles.ChipsWrapper>

        <Styles.DnsContentWrapper $expanded={!!selectedChip} data-testid={`dns-content-open-${selectedChip || 'false'}`}>
          <Styles.DnsContentDescription>
            {externalDomainLocale.dnsContentTitle}
          </Styles.DnsContentDescription>

          <Styles.DnsContainer>
            {originalNameServers && originalNameServers[0] !== 'ignore' && (
              <>
                <Styles.DnsContent>
                  <Styles.DnsCardTitle>
                    {externalDomainLocale.currentDns(selectedChipLabel)}
                  </Styles.DnsCardTitle>
                  <Styles.OriginalDnsContainer>
                    {originalNameServers && originalNameServers.map((item, index) => (
                      <Styles.OriginalDnsLabel key={`original-dns-${item}`}>
                        <Styles.StepLabel>
                          {externalDomainLocale.serverLabel(index + 1)}
                        </Styles.StepLabel>
                        <Styles.StepValue>
                          {item}
                        </Styles.StepValue>
                      </Styles.OriginalDnsLabel>
                    ))}
                  </Styles.OriginalDnsContainer>
                </Styles.DnsContent>
                <Styles.ArrowBox>
                  <IconChevron direction={mobile ? 'bottom' : 'right'} size="xl" color="primaryMedium" />
                </Styles.ArrowBox>
              </>
            )}

            <Styles.DnsContent>
              <Styles.DnsCardTitle>
                {externalDomainLocale.hostgatorDns}
              </Styles.DnsCardTitle>
              {hostNameServers && hostNameServers.map((nameServer, index) => (
                <Styles.StepDnsRow key={`hostgator-dns-${nameServer}`}>
                  <Styles.StepLabel>
                    {externalDomainLocale.serverLabel(index + 1)}
                  </Styles.StepLabel>
                  <Styles.StepValue data-testid={`hostgator-dns-${index}`}>
                    {nameServer}
                  </Styles.StepValue>

                  <Styles.IconButtonWrapper>
                    <IconButton
                      testId={`copy-${nameServer}`}
                      variant="white"
                      onClick={() => handleCopy(nameServer)}
                      icon="IconCopyFile"
                      changeFill={false}
                    />
                  </Styles.IconButtonWrapper>
                </Styles.StepDnsRow>
              ))}
            </Styles.DnsContent>
          </Styles.DnsContainer>

          {selectedChip !== PROVIDERS.OTHERS && (
            <Styles.ButtonsWrapper>
              <Button
                label={externalDomainLocale.configureDomain}
                disabled={!selectedChip}
                variant="secondary"
                width="fit"
                size="medium2"
                onClick={redirectToProvider}
                iconRight={<IconExternalLink color="primaryMedium" secondaryColor="transparent" />}
                testId="configure-domain-button"
                notFillSVG
              />
            </Styles.ButtonsWrapper>
          )}

          <Styles.DnsContentDescription>
            {externalDomainLocale.helpCenterLabel(externalDomainLocale.helpCenterUrls[selectedChip])}
          </Styles.DnsContentDescription>
        </Styles.DnsContentWrapper>
      </Styles.WizardContainer>

      <Styles.ButtonsWrapper>
        <Button
          label={externalDomainLocale.goBack}
          disabled={continueLoading}
          variant="tertiary"
          width="fit"
          size="medium2"
          onClick={handleReturnBack}
          testId="return-back-button"
        />
        <Button
          label={externalDomainLocale.continue}
          disabled={continueButtonDisabled}
          width="fit"
          size="medium2"
          onClick={handleContinue}
          loading={continueLoading}
          testId="continue-button"
        />
      </Styles.ButtonsWrapper>
    </Styles.ComponentWrapper>
  );
};

ExternalDomain.propTypes = {
  externalDomainLocale: PropTypes.object.isRequired,
  originalNameServers: PropTypes.array,
  hostNameServers: PropTypes.array,
  returnBack: PropTypes.func,
  continueToSuccess: PropTypes.func,
  continueLoading: PropTypes.bool,
};

export default ExternalDomain;
